import {
  CSSProperties,
  FC,
  memo,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { CountdownTimerContext } from "../../../components";
import { MatchStatus } from "../../../service";
import { getTeamColors } from "components/colors";
import { Text } from "components/common/Text";
import { TwoTeamScoreBoard } from "../../../components/common";

const TIMER_PAUSE_EVENTS: string[] = [];
const TIMER_HIDE_EVENTS: string[] = [];

const classNames = (...classes: (string | undefined | false)[]) =>
  classes.filter(Boolean).join(" ");

type ScoreStyle = CSSProperties & {
  ["--score-accent"]?: string;
};

const buildScoreStyle = (accent: string): ScoreStyle => ({
  "--score-accent": accent,
});

export const Timer: FC<{ status: MatchStatus }> = memo(
  ({ status: { nsc, c, pc, ...rest } }) => {
    const { minutes, seconds, pauseTimer, continueTimer, setDirection } =
      useContext(CountdownTimerContext);

    const [homeScore, awayScore] = useMemo(() => {
      const getPair = (v: any): [number, number] | null => {
        if (Array.isArray(v) && v.length >= 2) {
          const a = Number(v[0]) || 0;
          const b = Number(v[1]) || 0;
          return [a, b];
        }
        return null;
      };

      const totals = c && (c as any).T; // preferred total
      const score = c && (c as any).SCORE; // alternative
      const goal = c && (c as any).GOAL; // alternative naming in some feeds

      let pair = getPair(totals) || getPair(score) || getPair(goal);
      if (pair) return pair;

      // Fallback: sum quarters if present
      const qKeys = ["Q1", "Q2", "Q3", "Q4"] as const;
      let sum: [number, number] = [0, 0];
      let foundQuarter = false;
      for (const k of qKeys) {
        const p = getPair(c && (c as any)[k]);
        if (p) {
          sum = [sum[0] + p[0], sum[1] + p[1]];
          foundQuarter = true;
        }
      }
      if (foundQuarter) return sum;

      // Fallback: sum halves if present
      const h1 = getPair(c && (c as any).H1);
      const h2 = getPair(c && (c as any).H2);
      if (h1 || h2) {
        return [
          (h1?.[0] || 0) + (h2?.[0] || 0),
          (h1?.[1] || 0) + (h2?.[1] || 0),
        ];
      }

      return [0, 0];
    }, [c]);

    // --- score bump animation states ---
    const [homeBump, setHomeBump] = useState(false);
    const [awayBump, setAwayBump] = useState(false);

    useEffect(() => {
      setHomeBump(true);
      const id = window.setTimeout(() => setHomeBump(false), 650);
      return () => window.clearTimeout(id);
    }, [homeScore]);

    useEffect(() => {
      setAwayBump(true);
      const id = window.setTimeout(() => setAwayBump(false), 650);
      return () => window.clearTimeout(id);
    }, [awayScore]);

    // Pause / Continue
    useEffect(() => {
      if (nsc && TIMER_PAUSE_EVENTS.includes(nsc)) pauseTimer();
      else continueTimer();
    }, [nsc, pauseTimer, continueTimer]);

    useEffect(() => {
      setDirection(-1);
    }, [setDirection]);

    const shouldHideTimer = !!(nsc && TIMER_HIDE_EVENTS.includes(nsc));
    const { home: homeColor, away: awayColor } = getTeamColors(rest);
    const resolvedHome = homeColor;
    const resolvedAway = awayColor;

    // Quarter (Q1..Q4)
    const periodText =
      typeof pc === "number" && pc >= 0 && pc <= 4 ? `Q${pc}` : undefined;

    return (
      <div className="absolute left-1/2 top-0 mt-[0.5vw] flex -translate-x-1/2 flex-col items-start justify-center">
        <TwoTeamScoreBoard
          className="h-[6vw]"
          width="14vw"
          scoreHeight="6vw"
          accentHeight="1vw"
          gap="0.2vw"
          columnGap="0.2vw"
          homeAccentColor={resolvedHome}
          awayAccentColor={resolvedAway}
          homeAccentClassName="border-b-[0.2vw] border-[#1f3a4a]"
          awayAccentClassName="border-b-[0.2vw] border-[#1f3a4a]"
          homeScoreClassName={classNames(
            "basketball-score flex w-full items-center justify-center bg-white font-extrabold transition duration-200 ease-in-out",
            homeBump && "basketball-score-animate"
          )}
          awayScoreClassName={classNames(
            "basketball-score flex w-full items-center justify-center bg-white font-extrabold transition duration-200 ease-in-out",
            awayBump && "basketball-score-animate"
          )}
          homeScoreStyle={buildScoreStyle(resolvedHome)}
          awayScoreStyle={buildScoreStyle(resolvedAway)}
          homeContent={homeScore}
          awayContent={awayScore}
        />

        {!shouldHideTimer && (
          <div className="mt-[0.3vw] flex h-[4vw] w-[14vw] items-center justify-center gap-[0.2vw] bg-[rgba(0,0,0,0.8)] px-[0.4vw] text-center font-bold text-white">
            <Text
              variant="basketballTimer"
              className="flex items-center justify-center gap-[0.2vw]"
            >
              {periodText && (
                <span className="mr-[0.2vw] opacity-90">{periodText}</span>
              )}
              <span>{minutes}</span>
              <span>:</span>
              <span>{Number(seconds) < 10 ? `${seconds}` : seconds}</span>
            </Text>
          </div>
        )}
      </div>
    );
  }
);
